/**
 * The find-next bar: "3 of 12", previous, next, dismiss.
 *
 * Mounted once by each host at app level, NOT by the screen being searched —
 * a transaction editor and a document viewer both need it, neither should have
 * to know about it, and a bar that unmounted when the reader navigated between
 * two results would lose their place every time.
 *
 * It draws nothing until a search result has been opened (the highlight store
 * is empty otherwise), and it keeps drawing while the terms are active even
 * when the count is zero — "0 matches on this screen" is the answer to "why
 * isn't anything highlighted", and an invisible bar is not.
 *
 * Enter / Shift+Enter and F3 / Shift+F3 walk the matches, matching what a
 * browser's own find does, so the muscle memory is already right.
 */

import { useCallback, useEffect } from 'react'
import type { CSSProperties } from 'react'
import { ChevronDown, ChevronUp, Search, X } from 'lucide-react'
import { claimDock } from '../floatingDock'
import { UI_ATTR } from './highlight'
import { clearActiveHighlight, useActiveHighlight } from './highlightStore'
import { useSearchHighlight } from './useSearchHighlight'

export interface SearchMatchBarProps {
  /** Limit the scan (defaults to the whole document body). */
  root?: HTMLElement | null
  /** Hide the bar without clearing the terms — e.g. while the panel is open. */
  hidden?: boolean
}

export function SearchMatchBar({ root = null, hidden = false }: SearchMatchBarProps) {
  const active = useActiveHighlight()
  const terms = active?.terms || []
  const { count, index, next, previous, rescan, wordLevel } =
    useSearchHighlight(terms, { root, enabled: !!terms.length })

  const dismiss = useCallback(() => { clearActiveHighlight() }, [])

  useEffect(() => {
    if (!terms.length) return undefined
    const onKey = (event: KeyboardEvent) => {
      // Never steal Enter from someone typing: a form is the most likely thing
      // on screen behind this bar, and hijacking its submit key to jump to the
      // next match would be an unforgivable way to lose a record.
      const target = event.target as HTMLElement | null
      const typing = !!target && (
        target.tagName === 'INPUT' || target.tagName === 'TEXTAREA'
        || target.tagName === 'SELECT' || target.isContentEditable
      )
      if (event.key === 'F3') {
        event.preventDefault()
        if (event.shiftKey) previous(); else next()
        return
      }
      if (event.key === 'Escape' && !typing) {
        dismiss()
        return
      }
      if (event.key === 'Enter' && !typing) {
        event.preventDefault()
        if (event.shiftKey) previous(); else next()
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [terms.length, next, previous, dismiss])

  // Visible ⇒ it owns the corner, and whatever else docks there lifts above
  // it. ~44px tall plus its 18px inset; see `floatingDock`.
  useEffect(() => {
    if (!terms.length || hidden) return undefined
    return claimDock({ bottom: 62 })
  }, [terms.length, hidden])

  if (!terms.length || hidden) return null

  const position = count ? `${index + 1} of ${count}` : 'no matches here'

  return (
    <div {...{ [UI_ATTR]: '' }} style={bar} role="status" aria-live="polite">
      <Search size={14} style={{ opacity: 0.7, flexShrink: 0 }} />
      <span style={queryText} title={active?.query}>{active?.query}</span>
      <span style={{ ...countText, opacity: count ? 0.85 : 0.6 }}>{position}</span>
      <button
        type="button" onClick={previous} disabled={count < 2}
        title="Previous match (Shift+Enter)" aria-label="Previous match"
        style={{ ...iconBtn, opacity: count < 2 ? 0.35 : 1 }}
      >
        <ChevronUp size={15} />
      </button>
      <button
        type="button" onClick={next} disabled={count < 2}
        title="Next match (Enter)" aria-label="Next match"
        style={{ ...iconBtn, opacity: count < 2 ? 0.35 : 1 }}
      >
        <ChevronDown size={15} />
      </button>
      {!count ? (
        <button type="button" onClick={rescan} style={textBtn}
          title="The screen may still have been loading when this was checked">
          Check again
        </button>
      ) : null}
      {count && !wordLevel ? (
        <span style={noteText} title={
          'This browser cannot colour individual words, so each matching field '
          + 'is outlined instead.'
        }>
          fields outlined
        </span>
      ) : null}
      <button type="button" onClick={dismiss} title="Clear highlight (Esc)"
        aria-label="Clear highlight" style={iconBtn}>
        <X size={15} />
      </button>
    </div>
  )
}

const bar: CSSProperties = {
  position: 'fixed',
  right: 18,
  bottom: 18,
  zIndex: 9000,
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  padding: '6px 8px 6px 12px',
  borderRadius: 999,
  fontSize: 13.5,
  fontFamily: 'inherit',
  // Canonical token, not the `--color-surface` alias: this bar renders in
  // hosts that do not all define the aliases, and it must never come out dark
  // on a light page.
  background: 'var(--color-bg-secondary, #ffffff)',
  color: 'var(--color-text, #0f172a)',
  border: '1px solid var(--color-border, #e2e8f0)',
  boxShadow: '0 8px 28px rgba(15, 23, 42, 0.18)',
  maxWidth: 'min(560px, calc(100vw - 36px))',
}

const queryText: CSSProperties = {
  fontWeight: 600,
  maxWidth: 180,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
}

const countText: CSSProperties = {
  fontVariantNumeric: 'tabular-nums',
  whiteSpace: 'nowrap',
}

const noteText: CSSProperties = {
  fontSize: 13,
  opacity: 0.6,
  whiteSpace: 'nowrap',
  cursor: 'help',
}

const iconBtn: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 26,
  height: 26,
  borderRadius: 999,
  border: 'none',
  background: 'transparent',
  color: 'inherit',
  cursor: 'pointer',
  flexShrink: 0,
}

const textBtn: CSSProperties = {
  border: 'none',
  background: 'transparent',
  color: 'var(--color-accent, #2563eb)',
  cursor: 'pointer',
  fontSize: 13,
  padding: '0 2px',
  whiteSpace: 'nowrap',
}

export default SearchMatchBar
